/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import Big from 'big.js';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import type { ChangeEvent, FocusEventHandler } from 'react';
/**
 * Internal dependencies
 */
import { isInputValid, parseInput } from './validation';
import type { UseNumericInputProps } from './types';

export const useNumericInput = ({
  allowEmpty,
  canBeNegative,
  isFloat,
  max,
  min,
  onBlur,
  onChange,
  value,
}: UseNumericInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const oldValue = useRef(value);
  const revertToOriginal = useRef(false);
  const [currentValue, setCurrentValue] = useState(value);
  const options = useMemo(
    () => ({ allowEmpty, canBeNegative, isFloat, max, min }),
    [allowEmpty, canBeNegative, isFloat, max, min]
  );

  /**
   * Call external `onChange`
   */
  const handleBlur = useCallback(
    (ev: FocusEventHandler<HTMLInputElement>) => {
      let newValue = parseInput(oldValue.current, options);

      if (!revertToOriginal.current) {
        const parsedValue = parseInput(currentValue, options);

        if (parsedValue !== null) {
          newValue = parsedValue;
        }
      }

      revertToOriginal.current = false;
      if (newValue !== null) {
        setCurrentValue(newValue.numeric);

        if (onBlur) {
          onBlur(ev, newValue.numeric);
          return;
        }

        if (newValue.numeric !== oldValue.current) {
          onChange(ev, newValue.numeric);
        }
      }
    },
    [currentValue, onChange, options]
  );

  /**
   * Set internal state
   */
  const handleChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parseInput(ev.target.value, options);

      if (parsedValue !== null && onBlur && !parsedValue.isTemporary) {
        oldValue.current = parsedValue.numeric;
        onChange(ev, parsedValue.numeric);
      }

      setCurrentValue(parsedValue === null ? '' : parsedValue.display);
    },
    [onChange]
  );

  /**
   * Increment or decrement value using keyboard input
   */
  const handleKeyUpAndDown = useCallback(
    (ev: KeyboardEvent) => {
      const { altKey, key } = ev;
      const isCurrentValueValid = isInputValid(currentValue, options);

      let newValue = isCurrentValueValid
        ? parseInput(currentValue, options).numeric
        : 0;

      const diff = Big(options.isFloat && altKey ? 0.1 : 1);

      if (key === 'ArrowUp') {
        // Increment
        newValue =
          typeof max !== 'undefined'
            ? Math.min(max, Big(newValue).plus(diff))
            : Big(newValue).plus(diff);
      } else if (key === 'ArrowDown') {
        // Decrement
        newValue =
          typeof min !== 'undefined'
            ? Math.max(min, Big(newValue).minus(diff))
            : Big(newValue).minus(diff);
      }

      onChange(ev, Number(newValue));

      setCurrentValue(Number(newValue));
    },
    [currentValue, max, min, onChange, options]
  );

  /**
   * Blur input and revert value to original value
   */
  const handleEsc = useCallback(() => {
    setCurrentValue(oldValue.current);
    revertToOriginal.current = true;
    inputRef && inputRef.current?.blur();
  }, []);

  useEffect(() => {
    // update internal value when `value` prop changes
    oldValue.current = value;
    setCurrentValue(value);
  }, [value]);

  return {
    // state
    currentValue,
    inputRef,
    // event handlers
    handleBlur,
    handleChange,
    handleEsc,
    handleKeyUpAndDown,
    isIndeterminate: oldValue.current === currentValue,
  };
};
